import { useQuery } from "@tanstack/react-query";
import { Badge, Button, Card, ResponsiveGrid, Timestamp } from "@/design-system/components/ui";
import { notificationsApi } from "@/api/ux";
import { notificationDispatchKeys } from "@/hooks/notifications/notificationDispatchKeys";
import { useOptionalAuth } from "@/hooks/useAuth";

/**
 * Whether this session may see dispatch diagnostics at all.
 *
 * ONE predicate with two readers — this card, which self-gates so a caller who
 * mounts it without checking gets nothing rather than a 403, and the page that
 * decides whether to offer the toggle. Two copies of "is this an admin" is two
 * answers free to disagree, and the disagreement would be a button that reveals
 * nothing or a panel nobody can reach.
 *
 * `useOptionalAuth`, not `useAuth`: the latter THROWS outside an
 * `AuthProvider`, and `renderWithProviders` does not bootstrap one, so reaching
 * for it would take down every existing test of the host page. The
 * `AdminUndoButton` precedent — a permission-gated affordance self-gates and
 * simply does not render when auth is absent.
 */
export function useCanSeeDispatchDiagnostics(): boolean {
  const auth = useOptionalAuth();
  return auth?.hasRoleCode("ADMIN") ?? false;
}

/**
 * ADMIN-only: why staff notification email did or did not go out over a recent
 * window.
 *
 * This exists because `Notification.EmailDispatchError` had three writers and
 * ZERO readers — one of them (`AcsWebhookController`) carrying a comment that
 * cited a staff dashboard nobody had built — so a 100% dispatch outage was
 * invisible from inside the product and could only be found by a hand-written
 * SQL query against production. That is exactly what happened.
 *
 * The card reads and never writes — it holds no mutation at all, so the
 * stale-after-save gate has nothing to require of it and the explicit Recheck
 * is the only refresh affordance.
 */
export function NotificationDispatchDiagnosticsCard({ windowDays = 7 }: { windowDays?: number }) {
  const canSee = useCanSeeDispatchDiagnostics();
  const query = useQuery({
    queryKey: notificationDispatchKeys.diagnostics(windowDays),
    queryFn: () => notificationsApi.dispatchDiagnostics(windowDays),
    // Not merely a hidden panel: without this the request still fires and a
    // non-admin watches it 403.
    enabled: canSee,
  });

  if (!canSee) return null;

  const d = query.data;
  // Rows that were candidates for email at all. The in-app-only rows were never
  // one, so folding them in here would make a busy Transactional week read as a
  // mail problem.
  const emailEligible = d ? d.total - d.inAppOnly : 0;
  const allDeliberatelySkipped = !!d && emailEligible > 0 && d.skipped === emailEligible;

  return (
    <Card className="max-w-3xl">
      <div className="flex items-center justify-between gap-space-3 mb-space-3">
        <div className="min-w-0">
          <h2 className="font-semibold text-onyx m-0">Email dispatch diagnostics</h2>
          <p className="text-label text-gray-mid m-0 mt-[2px]">
            Every staff notification raised in the last {windowDays} days, and what the
            dispatcher did with it. Visible to administrators only.
          </p>
        </div>
        <Button
          size="sm"
          variant="tertiary"
          onClick={() => void query.refetch()}
          loading={query.isFetching && !query.isLoading}
        >
          Recheck
        </Button>
      </div>

      {query.isLoading ? (
        <p className="text-small text-gray-mid m-0">Loading diagnostics…</p>
      ) : query.isError || !d ? (
        <p className="text-small text-status-error m-0">Could not load dispatch diagnostics.</p>
      ) : d.total === 0 ? (
        <p className="text-small text-gray-mid m-0">
          No staff notifications were raised in this window, so there is nothing to report.
        </p>
      ) : (
        <div className="flex flex-col gap-space-3">
          {/* 🔴 THE ALARM IS "MARKED HANDLED, NOTHING SENT, NO REASON GIVEN" —
              never `sent === 0`. Info email is off by default, so a window of
              nothing but opted-out Info notifications sends zero emails and is
              a perfectly ordinary week. Keying on the zero got it backwards in
              both directions at once: it cried wolf on those ordinary weeks,
              and stayed silent on the real outage, whose rows carried a
              dispatch stamp and were counted as sent. */}
          {d.dispatchedNothingSent > 0 && (
            <div
              role="alert"
              className="rounded-lg border border-status-error bg-status-error/10 px-space-3 py-space-2"
            >
              <p className="text-small text-onyx m-0">
                <strong>
                  {d.dispatchedNothingSent}{" "}
                  {d.dispatchedNothingSent === 1 ? "notification was" : "notifications were"}{" "}
                  marked handled with no email sent
                </strong>{" "}
                and no reason recorded. That is a broken transport or a suppressed address rather
                than anyone&rsquo;s preference — check the sender configuration on this slot.
              </p>
            </div>
          )}

          {allDeliberatelySkipped && (
            <p className="text-small text-gray-mid m-0">
              Nothing needed emailing in this window: every notification raised was a deliberate
              skip.
            </p>
          )}

          {/* The six figures after Raised partition it exactly, which is why
              they all render even at zero — a reader can add them up and see
              that no row went unaccounted for. */}
          <dl className="m-0">
            <ResponsiveGrid cols={7} minColWidth="8rem" gap="sm">
              <Figure label="Raised" value={d.total} />
              <Figure label="Sent" value={d.sent} />
              <Figure label="Skipped" value={d.skipped} />
              <Figure
                label="Nothing sent"
                value={d.dispatchedNothingSent}
                tone={d.dispatchedNothingSent > 0 ? "error" : undefined}
              />
              <Figure label="In-app only" value={d.inAppOnly} />
              <Figure label="Pending" value={d.pending} />
              <Figure label="Stalled" value={d.stalled} tone={d.stalled > 0 ? "warning" : undefined} />
            </ResponsiveGrid>
          </dl>

          {d.notes.length > 0 && (
            <div className="flex flex-col gap-space-2">
              <h3 className="text-label uppercase tracking-label text-gray-mid m-0">
                Recorded reasons
              </h3>
              <ul className="flex flex-col gap-space-2 list-none p-0 m-0">
                {d.notes.map((n) => (
                  <li
                    key={`${n.category}:${n.note}`}
                    className="flex items-start justify-between gap-space-3 border-b border-border-light pb-space-2 last:border-b-0 last:pb-0"
                  >
                    <div className="min-w-0">
                      <p className="text-small text-onyx m-0 break-words">{n.note}</p>
                      <p className="text-label text-gray-mid m-0 mt-[2px]">
                        {n.category} · <Timestamp value={n.latestAt} prefix="last" />
                      </p>
                    </div>
                    <div className="flex items-center gap-space-2 shrink-0">
                      {/* A deliberate skip and a send failure are different
                          facts sharing one column — say which this is. */}
                      <Badge status={n.isDeliberateSkip ? "archived" : "error"}>
                        {n.isDeliberateSkip ? "Skipped" : "Failed"}
                      </Badge>
                      <span className="text-small font-semibold text-onyx tabular-nums">
                        {n.count}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-label text-gray-mid m-0">
            Since <Timestamp value={d.since} absolute />. A notification counts as{" "}
            <em>sent</em> only once a provider accepted it and returned a message id; a bounce
            recorded afterwards by the mail provider appears here as a failure reason without
            moving it back.
          </p>
        </div>
      )}
    </Card>
  );
}

function Figure({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "error" | "warning";
}) {
  const valueClass =
    tone === "error" ? "text-status-error" : tone === "warning" ? "text-status-warning" : "text-onyx";
  return (
    <div className="flex flex-col gap-[2px]">
      <dt className="text-label uppercase tracking-label text-gray-mid">{label}</dt>
      <dd className={`text-lead font-semibold tabular-nums m-0 ${valueClass}`}>{value}</dd>
    </div>
  );
}
